import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { DistributionScheduleService } from '../../../domain/services/distribution/distribution-schedule.service';
import {
  DayOption,
  DEFAULT_RANGES,
  DistributionSchedule,
  DistributionScheduleRequest,
  dayOptions,
  LAST_DAY,
} from '../../../domain/models/distribution-schedule.model';

/**
 * Distribution schedule edit screen (distribution design section 12 — Figma img 8–12).
 * "Start on" MM/YYYY picker, optional end month, and exactly two day-range rows
 * (start/end day dropdowns incl. "Last day"). Save → confirm dialog → success toast.
 */
@Component({
  selector: 'app-distribution-schedule-edit',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatDatepickerModule,
    MatProgressBarModule, MatSnackBarModule,
  ],
  templateUrl: './distribution-schedule-edit.component.html',
  styleUrl: './distribution-schedule-edit.component.css',
})
export class DistributionScheduleEditComponent implements OnInit {
  form: FormGroup;
  readonly days: DayOption[] = dayOptions();

  id: number | null = null;
  loading = false;
  saving = false;
  error: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly scheduleService: DistributionScheduleService,
    private readonly dialogService: DialogService,
    private readonly snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      startMonth: [null as Date | null, Validators.required],
      endMonth: [null as Date | null],
      range0Start: [DEFAULT_RANGES[0].startDay, Validators.required],
      range0End: [DEFAULT_RANGES[0].endDay, Validators.required],
      range1Start: [DEFAULT_RANGES[1].startDay, Validators.required],
      range1End: [DEFAULT_RANGES[1].endDay, Validators.required],
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.id = idParam ? Number(idParam) : null;

    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/distributions', label: 'Administrative' },
      { icon: 'pie_chart', url: '/app/distributions', label: 'Distributions' },
      { icon: this.id ? 'edit' : 'add', url: '/app/distributions', label: this.id ? 'Edit' : 'New' },
    ]);

    if (this.id) {
      this.loadExisting(this.id);
    }
  }

  get isEdit(): boolean {
    return this.id != null;
  }

  private loadExisting(id: number): void {
    this.loading = true;
    this.scheduleService.get(id).subscribe({
      next: (s) => { this.patch(s); this.loading = false; },
      error: () => { this.error = 'Failed to load schedule.'; this.loading = false; },
    });
  }

  private patch(s: DistributionSchedule): void {
    const r0 = s.ranges[0];
    const r1 = s.ranges[1];
    this.form.patchValue({
      startMonth: this.toDate(s.startPeriod),
      endMonth: s.endPeriod ? this.toDate(s.endPeriod) : null,
      range0Start: r0?.startDay ?? DEFAULT_RANGES[0].startDay,
      range0End: r0?.endDay ?? DEFAULT_RANGES[0].endDay,
      range1Start: r1?.startDay ?? DEFAULT_RANGES[1].startDay,
      range1End: r1?.endDay ?? DEFAULT_RANGES[1].endDay,
    });
  }

  // Material month-picker plumbing: select a month in the multi-year view and close.
  onMonthSelected(date: Date, control: 'startMonth' | 'endMonth', picker: MatDatepicker<Date>): void {
    this.form.get(control)!.setValue(date);
    picker.close();
  }

  clearEnd(): void {
    this.form.get('endMonth')!.setValue(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogService.openConfirmationDialog(
      'Save changes',
      'You want to save the changes to this distribution schedule?',
      (confirmed: boolean) => {
        if (confirmed) {
          this.persist();
        }
      },
    );
  }

  private persist(): void {
    this.saving = true;
    this.error = null;
    const body = this.buildRequest();
    const obs = this.isEdit
      ? this.scheduleService.update(this.id!, body)
      : this.scheduleService.create(body);
    obs.subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Distribution changes saved', 'Dismiss', { duration: 4000 });
        this.router.navigate(['/app/distributions']);
      },
      error: (e) => {
        this.saving = false;
        this.error = e?.error?.message || 'Failed to save schedule.';
      },
    });
  }

  private buildRequest(): DistributionScheduleRequest {
    const v = this.form.value;
    return {
      startPeriod: this.firstOfMonth(v.startMonth),
      endPeriod: v.endMonth ? this.firstOfMonth(v.endMonth) : null,
      ranges: [
        { startDay: v.range0Start, endDay: v.range0End },
        { startDay: v.range1Start, endDay: v.range1End },
      ],
    };
  }

  cancel(): void {
    this.router.navigate(['/app/distributions']);
  }

  /** Date -> yyyy-MM-01 (local, no timezone shift). */
  private firstOfMonth(d: Date): string {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}-01`;
  }

  /** yyyy-MM-01 -> Date (local). */
  private toDate(period: string): Date {
    const [y, m] = period.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }
}
